import React, { useContext, useEffect, useState } from 'react';
import catPriceAdapter from '@core-ui/price-case/adapters/category-price';
import ProductImage from '@cat/product-image';
import ProductCard from '@cat/product-card';
import ProductMarketingFlag from '@cat/product-marketing-flag';
import ProductName from '@cat/product-name';
import { BreakpointContext, LARGE, MEDIUM, XLARGE } from '@core-ui/breakpoint-provider';
import { Price } from '@core-ui/product-price';
import { ClpProductCardProps } from '@src/types/product-card';
import { PriceWrapper } from '../product-price-wrapper/price-wrapper';
import { ProductInfoWrapper } from '../product-info-wrapper';
import { useClpState } from '../../providers/app-state-provider';
import { useBRRedesign } from '../../hooks/br-redesign.hook';
import { useMarketingFlag } from '../../hooks/product-marketing-flag.hook';
import { getProductImage } from './collaborators/product-image.adapter';
import { handleProductClick } from './collaborators/handle-product-click';
import { productLinkAdapter } from './collaborators/product-link.adapter';

export const CLPProductCard = ({
  cardIndex,
  cid,
  groupIndex,
  hoverAlt,
  hoverSrc,
  highPriorityEvents,
  locale,
  pageIndex,
  pcid,
  productInfo,
  productGridSource,
  totalCount
}: ClpProductCardProps) => {
  const [selectedProduct, setSelectedProduct] = useState(productInfo);

  const { brand } = useClpState();
  const isBRRedesign = useBRRedesign();
  const showProductMarketingFlag = useMarketingFlag();

  const { minWidth, maxWidth, greaterOrEqualTo } = useContext(BreakpointContext);
  const isSmall = maxWidth(MEDIUM);
  const isMedium = minWidth(MEDIUM) && maxWidth(LARGE);
  const isLarge = greaterOrEqualTo(LARGE);
  const isXLarge = minWidth(XLARGE);
  const productImage = getProductImage({ productInfo, isBRRedesign });

  useEffect(() => {
    setSelectedProduct(productInfo);
  }, [productImage]);

  const productName = selectedProduct.name || selectedProduct.altText;
  const productUrl = productLinkAdapter({
    product: selectedProduct,
    cid,
    pcid,
    productGridSource,
    cardIndex,
    totalCount,
    pageIndex,
  });

  return (
    <article
      data-testid="composable-product-card"
      className="product-card composable-product-card"
    >
      <ProductCard
        productInfo={selectedProduct}
      >
        <ProductImage
          alt={productInfo.altText}
          data-testid="product-image"
          highPriorityEvents={highPriorityEvents || []}
          hoverAlt={hoverAlt}
          hoverSrc={hoverSrc}
          id={`product${productInfo.productID}`}
          index={cardIndex + groupIndex}
          name={productName}
          // @ts-ignore
          // TODO: Currently the type on cat page package is wrong. Should be fixed.
          onClick={handleProductClick}
          productUrl={productUrl}
          src={productImage}
        />
        <ProductInfoWrapper isBRRedesignEnabled={isBRRedesign}>
          <ProductName
            clickable={!selectedProduct.outOfStock}
            name={productName}
            onClick={() => handleProductClick({ productInfo, pcid })}
            link={productUrl}
            brand={brand}
            breakPoints={{ isMedium, isSmall, isLarge, isXLarge }}
          />
          <PriceWrapper isBRRedesignEnabled={isBRRedesign}>
            <Price
              adapter={catPriceAdapter}
              brand={brand}
              locale={locale}
              price={productInfo.price}
            />
          </PriceWrapper>
          {
            showProductMarketingFlag &&
            <ProductMarketingFlag
              marketingFlag={productInfo?.marketingFlag}
              breakPoints={{ isMedium, isSmall, isLarge, isXLarge }}
              brand={brand}
            />
          }
        </ProductInfoWrapper>
      </ProductCard>
    </article>
  );
};
